import { useEffect } from 'react';

const SITE_NAME = 'Lawra AI';
const BASE_URL = 'https://lawra-ai.com';

/**
 * Directly updates existing meta tags in the DOM.
 * This approach works reliably in SPAs because it modifies
 * the same tags that exist in index.html rather than adding duplicates.
 */
const usePageSEO = ({
  title,
  description,
  path = '/',
  ogType = 'website',
  ogImage,
  structuredData,
}) => {
  const fullTitle = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
  const canonicalUrl = `${BASE_URL}${path}`;
  const imageUrl = ogImage || `${BASE_URL}/logo-original.png`;

  useEffect(() => {
    // Store originals for cleanup
    const prevTitle = document.title;

    const getMeta = (attr, value) =>
      document.querySelector(`meta[${attr}="${value}"]`);
    const getLink = (rel) =>
      document.querySelector(`link[rel="${rel}"]`);

    const setMeta = (attr, key, content) => {
      let el = getMeta(attr, key);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    const setLink = (rel, href) => {
      let el = getLink(rel);
      if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', rel);
        document.head.appendChild(el);
      }
      el.setAttribute('href', href);
    };

    // Title
    document.title = fullTitle;

    // Primary meta
    setMeta('name', 'title', fullTitle);
    setMeta('name', 'description', description);

    // Canonical
    setLink('canonical', canonicalUrl);

    // Open Graph
    setMeta('property', 'og:type', ogType);
    setMeta('property', 'og:url', canonicalUrl);
    setMeta('property', 'og:title', fullTitle);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:image', imageUrl);

    // Twitter
    setMeta('property', 'twitter:card', 'summary_large_image');
    setMeta('property', 'twitter:url', canonicalUrl);
    setMeta('property', 'twitter:title', fullTitle);
    setMeta('property', 'twitter:description', description);
    setMeta('property', 'twitter:image', imageUrl);

    // Structured data — managed via a single tagged script
    const SD_ID = 'page-structured-data';
    let sdScript = document.getElementById(SD_ID);
    if (structuredData) {
      if (!sdScript) {
        sdScript = document.createElement('script');
        sdScript.id = SD_ID;
        sdScript.type = 'application/ld+json';
        document.head.appendChild(sdScript);
      }
      sdScript.textContent = JSON.stringify(structuredData);
    } else if (sdScript) {
      sdScript.remove();
    }

    // Cleanup — restore homepage defaults on unmount
    return () => {
      document.title = prevTitle;
    };
  }, [fullTitle, description, canonicalUrl, ogType, imageUrl, structuredData]);
};

export { usePageSEO, SITE_NAME, BASE_URL };
export default usePageSEO;
